import { useEffect, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../Toast';
import { VideoPlayer } from '../VideoPlayer';
import {
  requestStreamUploadUrl,
  uploadToStream,
  createVideoMedia,
  MAX_VIDEO_BYTES,
} from '../../lib/stream-queries';
import { fetchWelcomeVideo, saveWelcomeVideo } from '../../lib/welcome';

/**
 * Admin: upload / replace / clear the one-time founding-member welcome video.
 * A single landscape (16:9) clip, stored as a cf_stream media row whose id is
 * saved in app_settings (key 'welcome_video'). Played on /welcome.
 */
export function AdminWelcome({ active, me }: { active: boolean; me: { id: string } }) {
  const toast = useToast();
  const fileInput = useRef<HTMLInputElement>(null);

  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mediaId, setMediaId] = useState<string | null>(null);

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [saving, setSaving] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const { media_id } = await fetchWelcomeVideo();
      setMediaId(media_id);
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to load the welcome video.');
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }

  useEffect(() => {
    if (active && !loaded) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  async function accessToken(): Promise<string> {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) throw new Error('Sign in first.');
    return token;
  }

  async function persist(id: string | null) {
    setSaving(true);
    try {
      const token = await accessToken();
      await saveWelcomeVideo(id, token);
      setMediaId(id);
      toast.success(id ? 'Welcome video set.' : 'Welcome video removed.');
    } catch (err: any) {
      toast.error(err?.message ?? 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  async function handleFile(file: File) {
    if (!file.type.startsWith('video/')) {
      toast.error('Please choose a video file.');
      return;
    }
    if (file.size > MAX_VIDEO_BYTES) {
      toast.error(`That video is too large (max ${Math.round(MAX_VIDEO_BYTES / 1024 ** 3)} GB).`);
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const { uploadURL, uid } = await requestStreamUploadUrl(file.size);
      await uploadToStream({ uploadURL, file, onProgress: (f) => setProgress(f) });
      const { mediaId: newId } = await createVideoMedia({
        uid,
        uploadedBy: me.id,
        aspectRatio: 16 / 9,
      });
      await persist(newId);
    } catch (err: any) {
      console.error('[admin/welcome] upload failed', err);
      toast.error(
        err?.code === 'stream_not_configured' || err?.message === 'stream_not_configured'
          ? "Video uploads aren't available right now (Stream isn't configured)."
          : err?.message ?? 'Upload failed.',
      );
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }

  const busy = uploading || saving || loading;

  return (
    <>
      <div className="admin__panel-head">
        <h2>Welcome video</h2>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={refresh}
          disabled={busy}
        >
          {loading ? '…' : 'Refresh'}
        </button>
      </div>

      <p className="admin__note" style={{ marginTop: 0 }}>
        Shown once to every <strong>founding member</strong> right after they finish onboarding,
        on their first visit. Upload a short <strong>landscape (16:9)</strong> clip: the team's
        hello. Replacing it here updates it everywhere; clearing it falls back to a plain text
        welcome.
      </p>

      {mediaId ? (
        <div style={{ maxWidth: 640, margin: 'var(--space-4) 0' }}>
          <VideoPlayer mediaId={mediaId} aspect={16 / 9} />
        </div>
      ) : (
        <p style={{ color: 'var(--text-soft)', margin: 'var(--space-4) 0' }}>
          No welcome video set yet. Founding members currently see a plain text welcome.
        </p>
      )}

      {uploading && (
        <div style={{ maxWidth: 640, marginBottom: 'var(--space-3)' }}>
          <div className="mediaup__bar" aria-hidden="true">
            <div className="mediaup__bar-fill" style={{ width: `${Math.round(progress * 100)}%` }} />
          </div>
          <p className="admin__note" style={{ marginTop: 'var(--space-2)' }}>
            Uploading… {Math.round(progress * 100)}%
          </p>
        </div>
      )}

      <div className="admin__row-actions" style={{ gap: 'var(--space-3)', marginTop: 'var(--space-2)' }}>
        <input
          ref={fileInput}
          type="file"
          accept="video/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.currentTarget.value = '';
            if (f) void handleFile(f);
          }}
        />
        <button
          type="button"
          className="btn btn--primary btn--sm"
          onClick={() => fileInput.current?.click()}
          disabled={busy}
        >
          {uploading ? '…' : mediaId ? 'Replace video' : 'Upload video'}
        </button>
        {mediaId && (
          <button
            type="button"
            className="btn btn--ghost btn--sm admin__remove"
            onClick={() => {
              if (window.confirm('Remove the welcome video? Founding members will see a plain text welcome until you set a new one.')) {
                void persist(null);
              }
            }}
            disabled={busy}
          >
            {saving ? '…' : 'Remove'}
          </button>
        )}
      </div>
    </>
  );
}
